import { addError } from '@shared/utils/logger';
import { getConfig } from '../../../config/config';
import type { GeocodeData } from './places.types';

const { locationServiceUri } = getConfig();

const logError = (message: string, details: unknown) => {
  addError(`[Geocoding] ${message}`, {
    details,
  });
};

type GeocodingResult = Readonly<{
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
  formatted_address: string;
  place_id: string;
  types: ReadonlyArray<string>;
  address_components?: ReadonlyArray<
    Readonly<{
      long_name: string;
      short_name: string;
      types: ReadonlyArray<string>;
    }>
  >;
}>;

type GeocodingResponse = {
  results: GeocodingResult[];
  status: string;
  error_message?: string;
};

type GeocodeOptions = Readonly<{
  address?: string;
  components?: string;
  bounds?: string;
  language?: string;
  region?: string;
  place_id?: string;
  latlng?: string;
}>;

export const normalizeResult = (result: GeocodingResult): GeocodeData => ({
  location: result.geometry.location,
  placeId: result.place_id,
  formattedAddress: result.formatted_address,
  types: result.types ?? [],
  addressComponents: (result.address_components ?? []).map(c => ({
    longName: c.long_name,
    shortName: c.short_name,
    types: c.types,
  })),
});

export const geocodeAddress = async (options: GeocodeOptions): Promise<GeocodingResult | null> => {
  const _geocodeAddress = async (): Promise<GeocodingResponse | null> => {
    const params = { ...options };

    // eslint-disable-next-line compat/compat
    const searchParams = new URLSearchParams(params);
    const url = `${locationServiceUri}/geocode/json?${searchParams}`;

    const response = await fetch(url);

    if (!response.ok) {
      logError('Geocoding failed', { error: response.statusText, options });
      return null;
    }

    const data = await response.json();

    return data;
  };

  try {
    const result = await _geocodeAddress();

    if (result?.status === 'OK' && result?.results?.length > 0) {
      return result.results[0];
    }

    if (result?.error_message) {
      logError('Geocoding failed', { error: result.error_message, options });
    }

    return null;
  } catch (error) {
    logError('Failed to geocode', { error, options });
    return null;
  }
};
